/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useEffect, useContext, useState, useRef, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { extend } from '@thd-nucleus/data-sources';
import { Alert, Typography, Link } from '@one-thd/sui-atomic-components';
import { CreateAProject } from '@thd-olt-component-react/project-forms';
import THDStorageUtils, { SESSION_STORAGE } from '@thd-olt-global/thd-storage-utils';
import classNames from 'classnames';
import { ProjectTile } from './project-tile/ProjectTile';
import { EmptyMessagingPro } from './empty-messaging/EmptyMessagingPro';
import { dataModel } from './dataModel';
import { useProjects } from './useProjects';
import { ToggleLayoutBar } from './toggle-layout-bar/ToggleLayoutBar';
import { ProjectSummaryContext } from './ProjectSummaryContext';
import { CardView } from './CardView';
import { TableView } from './TableView';
import { CardViewSortAndFilter } from './CardViewSortAndFilter';
import {
  PROJECT_STATUS_ACTIVE,
  PROJECT_STATUS_ARCHIVED,
  PROJECT_STATUS_DELETED,
  CARD_VIEW, TABLE_VIEW,
  PROJECT_VIEW_SESSION_KEY,
  INITIAL_TABLE_VIEW_PAGE_SIZE,
  CARD_VIEW_PAGE_SIZE,
  STATUS_SESSION_STORAGE_KEY,
  PROJECTS_PAGINATION
} from './project-summary-helper';
import { triggerAnalytics, hasActiveFilters } from '../helpers';

export const ProjectSummary = ({ isValidCustomer, isUnauthorizedProjectDetails }) => {
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [successAlertMessage, setSuccessAlertMessage] = useState(null);
  const [
    showIsUnauthorizedProjectDetails,
    setShowIsUnauthorizedProjectDetails,
  ] = useState(isUnauthorizedProjectDetails);
  const defaultView = isMobile ? CARD_VIEW : TABLE_VIEW;
  const [view, setView] = useState(defaultView);
  const [page, setPage] = useState(1);
  const defaultPageSize = isMobile ? CARD_VIEW_PAGE_SIZE : INITIAL_TABLE_VIEW_PAGE_SIZE;
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortCriteria, setSortCriteria] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState({
    projectName: '',
    projectNumber: '',
    projectStatus: ''
  });
  const refetched = useRef(false);

  const {
    projects, loading, error, refetchProjects, pagination, refetch// move to each view
  } = useProjects({
    page, pageSize, view, isValidCustomer, sortCriteria, filterCriteria
  });
  const { totalPages, totalResults } = pagination || {};

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('project-summary.ready'); }, []);

  useEffect(() => {
    // Clear the state after getting the state value
    if (isUnauthorizedProjectDetails && window) {
      window.history.replaceState({}, '');
    }
  }, []);

  useEffect(() => {
    if (error?.graphQLErrors?.length && !refetched.current) {
      refetched.current = true;
      refetchProjects({ page });
    }
  }, [error]);

  const reloadProject = (event) => {
    if (event.target.visibilityState === 'visible') {
      refetchProjects({ page });
    }
  };

  const pageChangeHanlderCardView = (_event, newPage) => {
    triggerAnalytics(PROJECTS_PAGINATION, { paginationType: 'projects: projects page: card view: pagination' });
    setPage(newPage);
  };

  const fetchAlertStatusChange = () => {
    try {
      const statusValue = THDStorageUtils.get(SESSION_STORAGE, { key: STATUS_SESSION_STORAGE_KEY });
      if (statusValue === PROJECT_STATUS_DELETED) {
        setSuccessAlertMessage('Project Successfully Deleted');
      }
      if (statusValue === PROJECT_STATUS_ARCHIVED) {
        setSuccessAlertMessage('Project Successfully Archived');
      }
      if (statusValue === PROJECT_STATUS_ACTIVE) {
        setSuccessAlertMessage('Project Successfully set to Active');
      }
      THDStorageUtils.remove(SESSION_STORAGE, { key: STATUS_SESSION_STORAGE_KEY });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching alert status change:', err);
    }
  };

  useEffect(() => {
    fetchAlertStatusChange();
    if (isConsumerApp) {
      window.addEventListener('visibilitychange', reloadProject);
    }
  }, []);

  const handleProjectCreation = (response) => {
    if (response?.id) {
      window.location.href = `/projects/${response.id}`;
    } else {
      refetchProjects({ page: 1 });
    }
  };

  const handleViewChange = (newView) => {
    setView(newView);
    sessionStorage.setItem(PROJECT_VIEW_SESSION_KEY, newView);
    setPage(1);
    setPageSize(newView === TABLE_VIEW ? INITIAL_TABLE_VIEW_PAGE_SIZE : CARD_VIEW_PAGE_SIZE);
    setSortCriteria([]);
    const tableMsg = 'projects: projects page: table view';
    const cardMsg = 'projects: projects page: card view';
    triggerAnalytics('projects: project summary view type', { viewType: newView === TABLE_VIEW ? tableMsg : cardMsg });
  };

  const handleApplyFilters = (filters) => {
    setFilterCriteria(filters);
    setPage(1); // Reset to first page when filters change
  };

  useEffect(() => {
    if (isMobile) {
      setView(CARD_VIEW);
      setPageSize(CARD_VIEW_PAGE_SIZE);
    } else {
      const newView = sessionStorage?.getItem(PROJECT_VIEW_SESSION_KEY) || TABLE_VIEW;
      setView(newView);
      setPageSize(newView === TABLE_VIEW ? INITIAL_TABLE_VIEW_PAGE_SIZE : CARD_VIEW_PAGE_SIZE);
    }
  }, []);

  const closeSuccessAlert = () => {
    setSuccessAlertMessage(null);
  };

  const context = {
  };

  const projectSummartWrapperClass = classNames(
    'sui-grid',
    'sui-grid-cols-4',
    'sui-items-center',
    isMobile ? 'sui-gap-4' : 'sui-gap-7'
  );

  // Enhanced loading check to prevent premature empty state rendering
  // This fixes the LCP issue where empty state briefly flashes before projects load
  // We consider it loading if: GraphQL is loading OR (no projects, no error, valid customer,
  // and no pagination data yet)
  const isActuallyLoading = loading || (
    !projects?.length
    && !error
    && isValidCustomer
    && !pagination?.totalResults
    && pagination?.totalResults !== 0
  );

  // Memoize the empty messaging condition to avoid recalculating on every render
  const shouldShowEmptyMessaging = useMemo(() => !projects?.length
    && !isActuallyLoading
    && !hasActiveFilters(filterCriteria), // Show empty messaging for errors as fallback
  [projects?.length, isActuallyLoading, filterCriteria]);

  // Only show EmptyMessagingPro if there are no projects AND no filters are applied
  if (shouldShowEmptyMessaging) {
    return (
      <EmptyMessagingPro onProjectCreation={handleProjectCreation} />
    );
  }

  return (
    <ProjectSummaryContext.Provider value={context}>
      <div data-component="ProjectSummary">
        { showIsUnauthorizedProjectDetails && (
          <div className="sui-col-span-4">
            <Alert
              status="error"
              size="large"
              onClose={() => setShowIsUnauthorizedProjectDetails(false)}
            >
              This project requires permission to view. Please contact the project owner to request access.
            </Alert>
          </div>
        ) }
        <div className="sui-flex sui-flex-col sui-gap-4">
          {/* eslint-disable-next-line max-len */}
          <div className={`sui-mb-6 ${isMobile ? 'sui-flex sui-flex-col sui-gap-4' : 'sui-flex sui-items-center sui-justify-between'}`}>
            {isMobile && (
              <div className="sui-flex sui-flex-col sui-gap-2 sui-mb-4">
                <CreateAProject onCreate={handleProjectCreation} />
                <Link
                  id="create-new-project-tour-button"
                  data-testid="create-new-project-tour-button"
                  component="button"
                  underline="always"
                >
                  Learn to create a new Project with our guided tour
                </Link>
              </div>
            )}
            <div>
              <Typography variant="h2" weight="display" className="sui-mb-4">
                Keep Every Job on Track - All in One Place
              </Typography>
              <Typography variant="body-lg" weight="normal" className="sui-text-gray-600">
                Manage materials, timelines, and team collaboration across every jobsite. {!isMobile && <br />}
                Your project your way - from quote to delivery.
              </Typography>
            </div>
            {!isMobile && (
              <div className="sui-flex sui-flex-col sui-gap-2 sui-min-w-44 md:sui-sui-min-w-64 lg:sui-min-w-72">
                <CreateAProject onCreate={handleProjectCreation} />
                <Link
                  id="create-new-project-tour-button"
                  data-testid="create-new-project-tour-button"
                  component="button"
                  underline="always"
                >
                  Learn to create a new Project with our guided tour
                </Link>
              </div>
            )}
          </div>
        </div>
        <div className={projectSummartWrapperClass}>
          <div className="sui-col-span-4" data-testid="project-summary">
            {
              successAlertMessage !== null && (
                <div className="sui-pt-1">
                  <Alert onClose={closeSuccessAlert} status="success">{successAlertMessage}</Alert>
                </div>
              )
            }
            {(view === CARD_VIEW) && (
              <CardViewSortAndFilter
                loading={loading}
                projects={projects}
                setSortCriteria={setSortCriteria}
                sortCriteria={sortCriteria}
                onPageChange={(event, newPage) => { setPage(newPage); }}
                isMobile={isMobile}
                filterCriteria={filterCriteria}
              />
            )}
            <ToggleLayoutBar
              projects={projects}
              loading={loading}
              handleViewChange={handleViewChange}
              currentView={view}
              onApplyFilters={handleApplyFilters}
              filterCriteria={filterCriteria}
              showLayoutToggles={!isMobile}
            />
            { view === CARD_VIEW
            && (
              <div className="sui-col-span-4">
                <CardView
                  projects={projects}
                  loading={loading}
                  onPageChange={pageChangeHanlderCardView}
                  totalPages={totalPages}
                  totalResults={totalResults}
                  page={page}
                />
              </div>
            )}
            { view === TABLE_VIEW
            && (
              <div className="sui-col-span-4" data-testid="projects-table-view">
                <TableView
                  projects={projects}
                  loading={loading}
                  page={page}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  totalResults={totalResults}
                  setSortCriteria={setSortCriteria}
                  sortCriteria={sortCriteria}
                  filterCriteria={filterCriteria}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </ProjectSummaryContext.Provider>
  );
};

ProjectSummary.displayName = 'ProjectSummary';

ProjectSummary.propTypes = {
  isValidCustomer: PropTypes.bool.isRequired,
  isUnauthorizedProjectDetails: PropTypes.bool.isRequired
};

ProjectSummary.dataModel = extend(
  dataModel,
  ProjectTile,
  CreateAProject,
  EmptyMessagingPro,
);
